<template>
  <div
    class="outreach-contact-lists"
    data-testid="outreach-contact-lists"
  >
    <div class="outreach-contact-lists__header">
      <BaseInputText
        v-model="searchText"
        class="outreach-contact-lists__search"
        clearable
        new-design
        :placeholder="$t('outreach_contact_lists.search_lists_placeholder')"
      >
        <template #prepend>
          <QIcon
            color="ds-denim-4"
            flat
            name="search"
            size="24px"
          />
        </template>
      </BaseInputText>
      <div
        v-if="!compact"
        class="outreach-contact-lists__filters-wrapper"
      >
        <div class="outreach-contact-lists__filters-section">
          <InputSelectWithCheckboxes
            v-model="tagIds"
            :all-options-label="
              $t('outreach_activity_filters.tags_placeholder')
            "
            :disable-options="noTags"
            :options="tags"
            :placeholder-text="tagsPlaceholder"
            searchable
            show-all-options
            show-icon
            show-ok-button
            @all-options="onAllTagOptions"
            @confirm="resetSearch"
            @update:model-value="resetSearch"
          >
            <template #beforeAllOptions>
              <div
                class="q-px-sm"
                @click.stop
              >
                <BaseCheckbox
                  v-model="noTags"
                  :label="$t('outreach_contact_lists.exclude_tags')"
                  label-class="full-width q-pl-sm"
                  left-label
                  new-design
                  @update:model-value="onNoTags"
                />
              </div>
              <BaseSeparator />
            </template>
            <template #afterSearchInput>
              <QSpinner
                v-show="loadingTags"
                class="full-width absolute justify-center"
                :size="30"
              />
            </template>
          </InputSelectWithCheckboxes>
          <InputSelectWithCheckboxes
            v-model="listVisibilityIds"
            emit-value
            :options="listVisibilityOptions"
            :placeholder-text="
              $t('outreach_contact_lists.select_lists_placeholder')
            "
            @update:model-value="resetSearch"
          />
          <InputSelectWithCheckboxes
            v-model="listCreatedByIds"
            emit-value
            :ok-button-label="$t('global.apply')"
            :options="listCreatedByOptions"
            :placeholder-text="
              $t('outreach_contact_lists.created_by_placeholder')
            "
            searchable
            show-ok-button
            @update:model-value="resetSearch"
          />
          <InputSelectWithCheckboxes
            v-model="listUpdatedByIds"
            emit-value
            :ok-button-label="$t('global.apply')"
            :options="listUpdatedByOptions"
            :placeholder-text="
              $t('outreach_contact_lists.updated_by_placeholder')
            "
            searchable
            show-ok-button
            @update:model-value="resetSearch"
          />
        </div>

        <div class="outreach-contact-lists__filters-section">
          <BaseButton
            color="secondary"
            icon="plus"
            :label="$t('outreach_contact_lists.add_list_label')"
            new-design
            new-icon
            @click="onAddList"
          />
          <BaseButtonGroup gap="px">
            <BaseStreemTooltip v-if="!canCreateMediaRelease">
              {{ addToMediaReleaseTooltip }}
            </BaseStreemTooltip>
            <BaseButton
              class="full-width"
              v-bind="primaryButtonAttributes"
              @click="primaryButtonAction"
            />
            <BaseButtonDropdown
              chevron
              close-on-click
              color="primary"
              :disabled="!canCreateMediaRelease"
              new-design
              new-dropdown
              placement="bottom-end"
            >
              <BaseButtonList
                :options="actionOptions"
                @update:model-value="actionOptionSelected"
              />
            </BaseButtonDropdown>
          </BaseButtonGroup>
        </div>
      </div>
    </div>

    <div class="column full-height no-wrap bg-white">
      <BaseLoadingBar
        v-if="loading && !contactLists.length"
        height="2"
      />

      <template v-else-if="contactLists.length">
        <div
          v-if="selectedIds.length"
          class="outreach-contact-lists__selection"
        >
          <OutreachRecordSelectedSection
            v-model="selectedIds"
            record-key="lists"
            view-modal="OutreachContactListSelectionModal"
            @refresh="onResourceUpdated"
          />
        </div>

        <OutreachContactListsTable
          :compact="compact"
          :contact-lists="contactLists"
          :filters="filters"
          :loading="loading"
          :selected-ids="selectedIds"
          @duplicate-contact-list="onContactListDuplicated"
          @resource-updated="onResourceUpdated"
          @update:selected-ids="updateSelectedIds"
          @update:sort-order="sortData"
        >
          <template #nameHeader>
            <BaseCheckbox
              v-if="!compact"
              v-model="selectAll"
              :aria-label="$t('global.select_all')"
              data-testid="select-all-checkbox"
              :indeterminate="isIndeterminate"
              new-design
            />
          </template>
        </OutreachContactListsTable>
      </template>

      <OutreachLaunchpadTableEmptyState
        v-else
        class="q-pt-lg"
      >
        <template #message>
          {{ $t("outreach_contact_lists.empty_list_table_message") }}
        </template>
      </OutreachLaunchpadTableEmptyState>

      <Portal to="outreach-lists-footer">
        <div class="tw:flex tw:justify-center tw:sm:justify-end">
          <BasePagination
            :pagination="pagination"
            @pagination-changed="onPaginationChange"
          />
        </div>
      </Portal>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";
import { Portal } from "portal-vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseButtonGroup,
  BaseButtonList,
  BaseInputText,
  BaseLoadingBar,
  BaseSeparator,
  BaseStreemTooltip,
} from "shared/components/base";
import BasePagination from "shared/components/base/BasePagination";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import InputSelectWithCheckboxes from "shared/components/core/forms/InputSelectWithCheckboxes.vue";
import useClipboard from "shared/composables/useClipboard";
import { useCreateOutreachPitch } from "shared/composables/useCreateOutreachPitch";
import useMediaRelease from "shared/composables/useMediaRelease";
import useOutreachTiers from "shared/composables/useOutreachTiers";
import useTeamPicker from "shared/composables/useTeamPicker";
import { CONTACT_EXPORT_LIMIT, CONTACT_LIST_LIMIT } from "shared/constants";
import { ContactList } from "shared/resources";
import { useGlobalStore } from "shared/stores/global";
import { useOutreachContactListsStore } from "shared/stores/outreachContactLists";

import OutreachContactListsTable from "src/components/Outreach/OutreachContactListsTable.vue";
import OutreachLaunchpadTableEmptyState from "src/components/Outreach/OutreachLaunchpadTableEmptyState.vue";
import OutreachRecordSelectedSection from "src/components/Outreach/OutreachRecordSelectedSection.vue";

export default {
  name: "OutreachContactLists",
  components: {
    BaseButton,
    BaseButtonDropdown,
    BaseButtonGroup,
    BaseButtonList,
    BaseSeparator,
    BaseCheckbox,
    InputSelectWithCheckboxes,
    BaseInputText,
    BaseLoadingBar,
    OutreachContactListsTable,
    OutreachLaunchpadTableEmptyState,
    OutreachRecordSelectedSection,
    BasePagination,
    Portal,
    BaseStreemTooltip,
  },
  props: {
    contactId: {
      type: [Number, String],
      default: null,
    },
    contactType: {
      type: String,
      default: "",
    },
    compact: {
      type: Boolean,
    },
  },
  setup() {
    const { copyToClipboard } = useClipboard();

    const {
      mediaRelease,
      setDefaultMediaRelease,
      buildMediaReleaseContactLists,
    } = useMediaRelease();

    const { hasOutreachMediaReleases, outreachUpgradeText } =
      useOutreachTiers();

    const { createOutreachPitchWithContactList } = useCreateOutreachPitch();
    const { appConfig } = storeToRefs(useGlobalStore());
    const { loadRecentContactLists } = useOutreachContactListsStore();

    const { selectedTeamId, selectedTeamPreference } = useTeamPicker();

    return {
      copyToClipboard,
      mediaRelease,
      setDefaultMediaRelease,
      buildMediaReleaseContactLists,
      hasOutreachMediaReleases,
      outreachUpgradeText,
      createOutreachPitchWithContactList,
      appConfig,
      loadRecentContactLists,
      selectedTeamId,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      loading: false,
      searchText: "",
      contactLists: [],
      selectedIds: [],
      filteredIds: [],
      listCreatedByIds: [],
      listUpdatedByIds: [],
      listVisibilityIds: [],
      noTags: false,
      tagIds: [],
      tags: [],
      contactListsContactsCounts: {},
      pagination: {
        page: 1,
        pageSize: 20,
        pageCount: 0,
      },
      filters: {
        showPrivate: 0,
        sortOrder: {
          sortBy: "updatedAt",
          sortDirection: "desc",
        },
      },
      debounceLoadContactLists: debounce(this.loadContactLists, 300),
    };
  },
  computed: {
    primaryButtonAttributes() {
      return this.hasOutreachMediaReleases
        ? {
            name: "send_media_release",
            label: this.$t("outreach_contact_lists.send_media_release_label"),
            color: "primary",
            icon: "create",
            newIcon: true,
            newDesign: true,
            disabled: Boolean(!this.canCreateMediaRelease),
          }
        : {
            name: "copy",
            label: this.$t("outreach_contact_lists.copy_emails_label"),
            color: "primary",
            icon: "copy",
            newIcon: true,
            newDesign: true,
            disabled: this.disableExport,
          };
    },
    listVisibilityOptions() {
      return [
        { value: 0, label: this.$t("global.public") },
        { value: 1, label: this.$t("global.private") },
      ];
    },
    listCreatedByOptions() {
      return this.sortUserOptions(this.createdByUsers(this.contactLists));
    },
    listUpdatedByOptions() {
      return this.sortUserOptions(this.updatedByUsers(this.contactLists));
    },
    selectAll: {
      get() {
        return (
          this.selectedIds.length > 0 &&
          this.filteredIds.every((id) => this.selectedIds.includes(id))
        );
      },
      set(value) {
        if (value) {
          this.selectedIds = [
            ...new Set([...this.selectedIds, ...this.filteredIds]),
          ];
        } else {
          this.selectedIds = [];
        }
      },
    },
    actionOptions() {
      const options = [];

      if (this.hasOutreachMediaReleases) {
        if (this.$features.has("has_outreach_ai_pitch")) {
          options.push({
            name: "send_pitch",
            label: this.$t("outreach_contact_lists.send_pitch_label"),
            icon: "ai-generate",
            newIcon: true,
            disabled: this.selectedIds.length !== 1,
            enableTooltip: this.selectedIds.length !== 1,
            tooltipText: this.$t("outreach_contact_lists.send_pitch_tooltip"),
          });
        }

        options.push({
          name: "copy",
          label: this.$t("outreach_contact_lists.copy_emails_label"),
          icon: "copy",
          newIcon: true,
          disabled: this.disableExport,
        });
      }

      options.push({
        name: "export",
        label: this.$t("outreach_contact_lists.download_csv_label"),
        icon: "download",
        newIcon: true,
        disabled: this.disableExport,
      });

      if (!options.find(({ name }) => name === "copy")) {
        options.unshift({
          name: "send_media_release",
          label: this.$t("outreach_contact_lists.send_media_release_label"),
          color: "primary",
          icon: "create",
          newIcon: true,
          newDesign: true,
          disabled: !this.hasOutreachMediaReleases,
          enableTooltip: !this.hasOutreachMediaReleases,
          tooltipText: this.outreachUpgradeText,
        });
      }

      options.push({
        name: "tags",
        label: getLocaleText("outreach_contact_lists.add_tags_label"),
        icon: "tag-mention",
        newIcon: true,
        disabled: this.disableExport,
      });

      return options;
    },
    isIndeterminate() {
      return this.selectedIds.length > 0 && !this.selectAll;
    },
    currentSelectedContactsCount() {
      let count = 0;

      this.selectedIds.forEach((selectedId) => {
        if (this.contactListsContactsCounts[selectedId] > 0) {
          count += this.contactListsContactsCounts[selectedId];
        }
      });

      return count;
    },
    disableExport() {
      return (
        !this.selectedIds.length ||
        this.currentSelectedContactsCount > CONTACT_EXPORT_LIMIT
      );
    },
    canCreateMediaRelease() {
      return (
        this.selectedIds.length &&
        this.currentSelectedContactsCount <= CONTACT_LIST_LIMIT
      );
    },
    addToMediaReleaseTooltip() {
      if (this.currentSelectedContactsCount > CONTACT_LIST_LIMIT) {
        return this.$t("outreach_contact_lists.select_contacts_tooltip", [
          CONTACT_LIST_LIMIT,
        ]);
      }

      if (!this.selectedIds.length) {
        return this.$t("outreach_contact_lists.select_a_contact_tooltip");
      }

      return "";
    },
    tagsPlaceholder() {
      if (this.noTags) {
        return getLocaleText("outreach_contact_lists.exclude_tags");
      }

      return getLocaleText("outreach_activity_filters.tags_placeholder");
    },
  },
  watch: {
    searchText: "resetSearch",
  },
  mounted() {
    this.debounceLoadContactLists();
    this.loadTags();
  },
  beforeUnmount() {
    this.debounceLoadContactLists.cancel();
  },
  methods: {
    sortData(sortOrder) {
      this.filters.sortOrder = sortOrder;
      this.debounceLoadContactLists();
    },
    createdByUsers(contactLists) {
      const users = contactLists.map((contactList) => ({
        value: contactList.user.id,
        firstName: contactList.user.firstName,
        lastName: contactList.user.lastName,
        label: contactList.user.name,
      }));

      return this.uniqueUsers(users);
    },
    updatedByUsers(contactLists) {
      const users = contactLists
        .filter((contactList) => Boolean(contactList.updatedByUser?.id))
        .map((contactList) => ({
          value: contactList.updatedByUser?.id,
          firstName: contactList.updatedByUser?.firstName,
          lastName: contactList.updatedByUser?.lastName,
          label: contactList.updatedByUser?.name,
        }));

      return this.uniqueUsers(users);
    },
    uniqueUsers(users) {
      return users.filter(
        (object, index, self) =>
          index === self.findIndex((objectB) => objectB.value === object.value)
      );
    },
    sortUserOptions(options) {
      const sortedOptions = Array.from(options).sort((optionOne, optionTwo) => {
        const lastNameA = optionOne.lastName;
        const lastNameB = optionTwo.lastName;

        return lastNameA?.localeCompare(lastNameB) || 0;
      });

      return Array.from(sortedOptions);
    },
    primaryButtonAction() {
      if (this.primaryButtonAttributes.name === "send_media_release") {
        this.createMediaRelease();
      } else {
        this.onExportContacts(this.primaryButtonAttributes.name);
      }
    },
    visibilityOption() {
      if (this.listVisibilityIds.length === 1) {
        if (this.listVisibilityIds.includes(1)) {
          return 1;
        }

        return 0;
      }

      return null;
    },
    async loadContactLists() {
      try {
        if (this.noTags) {
          this.tagIds = [];
        }

        const filters = {
          name: { match: this.searchText },
          user_id: { eq: this.listCreatedByIds },
          updated_by_user_id: { eq: this.listUpdatedByIds },
          private: { eq: this.visibilityOption() },
          ...(this.$features.has("has_team_workspaces")
            ? { organisationTeamId: [this.selectedTeamPreference, "null"] }
            : {}),
          tag_id: { eq: this.tagIds },
          without_tags: this.noTags,
        };

        if (this.contactId && this.contactType) {
          filters.contact = {
            eq: {
              id: this.contactId,
              type: this.contactType,
            },
          };
        }

        this.loading = true;

        const response = await ContactList.includes([
          "user",
          "updated_by_user",
          "tags",
        ])
          .selectExtra([
            "contactListsContactsCount",
            "mediaReleasesContactListsCount",
            "pitchesContactListsCount",
            "tagsCount",
          ])
          .where(filters)
          .per(this.pagination.pageSize)
          .page(this.pagination.page)
          .order({
            [this.filters.sortOrder.sortBy]:
              this.filters.sortOrder.sortDirection,
          })
          .stats({
            total: "count",
            select_all: ["lists_count", "contacts_count"],
          })
          .all();

        this.contactLists = response.data;

        this.filteredIds = Object.keys(
          response.meta.stats.select_all.contacts_count
        );

        this.contactMailingListsContactsCount =
          response.meta.stats.select_all.contacts_count;

        this.pagination.pageCount = Math.ceil(
          this.filteredIds.length / this.pagination.pageSize
        );
      } catch (error) {
        this.$error(this.$t("outreach_contact_lists.load_list_error"), error);
      } finally {
        this.loading = false;
      }
    },
    onAddList() {
      this.$modals.open("OutreachContactListModal", {
        props: {
          listType: "new",
        },
        events: {
          save: async (newContactList) => {
            this.$modals.close("OutreachContactListModal");

            await this.debounceLoadContactLists();
            await this.refreshRecentContactLists();
            await this.loadTags();

            this.$dialog
              .confirm({
                title: this.$t("outreach_contact_lists.add_list_confirm_title"),
                dialogMaxWidth: "350px",
                message: this.$t(
                  "outreach_contact_lists.add_list_confirm_message",
                  { name: newContactList.name }
                ),
                confirmLabel: this.$t(
                  "outreach_contact_lists.add_list_confirm_label"
                ),
              })
              .onOk(() => {
                this.routeToContacts();
              });
          },
        },
      });
    },
    routeToContacts() {
      this.$safeRouterPush({
        name: "outreach-search",
      });
    },
    onPaginationChange(updatedPagination) {
      this.pagination = updatedPagination;
      this.debounceLoadContactLists();
    },
    onResourceUpdated() {
      this.debounceLoadContactLists();
      this.refreshRecentContactLists();
      this.loadTags();
    },
    async refreshRecentContactLists() {
      try {
        const organisationTeamId = this.$features.has("has_team_workspaces")
          ? [this.selectedTeamId, "null"].filter(
              (teamId) => teamId !== undefined
            )
          : undefined;

        await this.loadRecentContactLists({
          organisationTeamId,
          reload: true,
        });
      } catch (error) {
        this.$error(
          getLocaleText("outreach_contact_lists.load_list_error"),
          error
        );
      }
    },
    onContactListDuplicated() {
      this.sortOrder = {
        sortBy: "name",
        sortDirection: "asc",
      };
    },
    async actionOptionSelected(option) {
      if (option.name === "send_pitch") {
        const selectedList = this.contactLists.find(
          (contactList) => contactList.id === this.selectedIds[0]
        );

        try {
          await this.createOutreachPitchWithContactList(selectedList);
        } catch (error) {
          this.$error(this.$t("outreach_pitch.error_creating_pitch"), error);
        }

        return;
      }

      if (option.name === "tags") {
        this.$modals.open("OutreachContactListTagsModal", {
          props: {
            contactListIds: this.selectedIds,
          },
          events: {
            tags: () => {
              this.$modals.close("OutreachContactListTagsModal");
              this.debounceLoadContactLists();
              this.loadTags();
            },
          },
        });

        return;
      }

      this.onExportContacts(option.name);
    },
    async copyListEmails() {
      try {
        const { data } = await this.$streemApiV1.get(
          "contact_lists/copy_emails",
          {
            params: {
              contact_list_ids: this.selectedIds,
            },
          }
        );

        this.selectedListEmails = data;
        this.copyToClipboard(this.selectedListEmails.join(","));
      } catch (error) {
        this.$error(this.$t("outreach_contact_lists.copy_email_error"), error);
      }
    },
    onExportContacts(action) {
      this.$dialog
        .confirm({
          theme: "default",
          dialogMaxWidth: "450px",
          title: this.$t("outreach_contact_lists.export_confirm_title"),
          message: this.$t("outreach_export_disclaimer", {
            platformName: this.appConfig.platformName,
          }),
        })
        .onOk(() => {
          if (action === "copy") {
            this.copyListEmails();
          } else if (action === "export") {
            this.exportContacts();
          }
        });
    },
    async exportContacts() {
      try {
        this.exporting = true;

        const response = await this.$streemApiV1.post(
          "contact_lists/export_csv",
          {
            params: {
              contact_list_ids: this.selectedIds,
            },
            responseType: "blob",
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `export_${new Date().getTime()}.csv`;
        link.click();
      } catch (error) {
        this.$error(this.$t("outreach_contact_lists.export_error"), error);
      } finally {
        this.exporting = false;
      }
    },
    async createMediaRelease() {
      this.setDefaultMediaRelease();

      this.mediaRelease.mediaReleasesContactLists =
        this.buildMediaReleaseContactLists(this.selectedIds);

      try {
        await this.mediaRelease.saveResource({
          with: "mediaReleasesContactLists",
        });

        this.$safeRouterPush({
          name: "media-release-edit",
          params: {
            id: this.mediaRelease.id,
          },
        });
      } catch (error) {
        this.$error(
          this.$t("outreach_contact_lists.create_media_release_error"),
          error
        );
      }
    },
    updateSelectedIds(id) {
      if (this.selectedIds.includes(id)) {
        this.selectedIds.splice(this.selectedIds.indexOf(id), 1);
      } else {
        this.selectedIds.push(id);
      }
    },
    resetSearch() {
      this.pagination.page = 1;
      this.debounceLoadContactLists();
    },
    async loadTags() {
      try {
        this.loadingTags = true;

        const params = {
          ...(this.$features.has("has_team_workspaces")
            ? { organisation_team_id: this.selectedTeamPreference }
            : {}),
        };

        this.tags = (
          await this.$streemApiV1.get("contact_lists/tags", { params })
        ).data;
      } catch (error) {
        this.$error(
          this.$t("outreach_activity_filters.load_tags_error"),
          error
        );
      } finally {
        this.loadingTags = false;
      }
    },
    onNoTags(value) {
      if (value) {
        this.tagIds = [];
      }
    },
    onAllTagOptions(value) {
      if (value) {
        this.noTags = false;
      } else {
        this.tagIds = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.outreach-contact-lists {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-top: 1.5rem;

  &__header {
    display: grid;
    gap: var(--s-spacing-md) var(--s-spacing-lg);
    grid-template: var(--grid-template);
    grid-template-areas: var(--grid-template-areas);
    justify-content: flex-start;
  }

  &__select-all {
    grid-area: select;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__search {
    flex: 1;
    max-width: 250px;

    @media screen and (width <= 600px) {
      max-width: 100%;
    }
  }

  &__filters {
    &-wrapper {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }

    &-section {
      display: flex;
      gap: 1rem;

      @media screen and (width <= 600px) {
        width: 100%;
        flex-direction: column;
      }
    }
  }

  &__selection {
    display: flex;
    padding-bottom: var(--s-spacing-lg);
  }
}
</style>
